import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Filter, Search, Star as StarIcon, TrendingUp, LayoutGrid, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import HcahpsStars from "@/components/HcahpsStars";
import HcahpsHospitalDialog from "@/components/HcahpsHospitalDialog";
import { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import { stateForCity } from "@/lib/indianStates";
import api from "@/lib/api";

const TIERS = ["all", "platinum", "gold", "silver", "bronze"];
const TIER_LABEL = { platinum: "Platinum", gold: "Gold", silver: "Silver", bronze: "Bronze" };
const TIER_STYLE = {
  platinum: "bg-[#0b3d3a] text-[#f6f5f0]",
  gold: "bg-[#c45f4a] text-[#f6f5f0]",
  silver: "bg-slate-300 text-slate-900",
  bronze: "bg-amber-800/80 text-amber-50",
};

export default function HcahpsLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [tier, setTier] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [view, setView] = useState("overall"); // "overall" | "by-state"
  const [dialogHospital, setDialogHospital] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/hospitals");
        const withState = (Array.isArray(data) ? data : []).map((h) => ({
          ...h,
          state: stateForCity(h.city),
        }));
        setRows(withState);
      } catch (err) {
        console.error("Failed to load HCAHPS leaderboard:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cities = useMemo(
    () => ["all", ...Array.from(new Set(rows.map((r) => r.city).filter(Boolean))).sort()],
    [rows]
  );
  const states = useMemo(
    () => ["all", ...Array.from(new Set(rows.map((r) => r.state).filter(Boolean))).sort()],
    [rows]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => (r.hcahps_score ?? null) !== null)
      .filter((r) => (city === "all" ? true : r.city === city))
      .filter((r) => (tier === "all" ? true : r.tier === tier))
      .filter((r) => (stateFilter === "all" ? true : r.state === stateFilter))
      .filter((r) =>
        !needle ? true : (r.name || "").toLowerCase().includes(needle) || (r.city || "").toLowerCase().includes(needle) || (r.state || "").toLowerCase().includes(needle)
      )
      .sort((a, b) => (b.hcahps_score || 0) - (a.hcahps_score || 0));
  }, [rows, q, city, tier, stateFilter]);

  const byState = useMemo(() => {
    if (view !== "by-state") return [];
    const groups = new Map();
    filtered.forEach((h) => {
      const key = h.state || "Other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(h);
    });
    return Array.from(groups.entries())
      .map(([state, hospitals]) => ({
        state,
        hospitals,
        avg: Math.round(hospitals.reduce((a, x) => a + (x.hcahps_score || 0), 0) / hospitals.length),
        top: hospitals[0],
      }))
      .sort((a, b) => b.avg - a.avg);
  }, [view, filtered]);

  const podium = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const avg = filtered.length
    ? Math.round(filtered.reduce((a, r) => a + (r.hcahps_score || 0), 0) / filtered.length)
    : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="hcahps-leaderboard">
      <header className="border-b border-border bg-white/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" aria-label="Arogya Pramana home">
            <BrandMark size={42} />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/directory" className="hover:text-primary">Directory</Link>
            <Link to="/hcahps" className="text-primary font-medium">HCAHPS</Link>
            <Link to="/network" className="hover:text-primary">Change Makers</Link>
            <Link to="/startups" className="hover:text-primary">Startups</Link>
            <Link to="/login" className="hover:text-primary">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Patient Experience · HCAHPS-aligned</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-primary leading-tight">
            Hospitals ranked by patient voice
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground leading-relaxed">
            Every hospital on Arogya Pramana is scored using an HCAHPS-aligned instrument — 10 composite and single-item measures translated to a 1–5 star rating. Sorted by overall patient experience score. No advertising, no pay-to-rank.
          </p>
          <div className="mt-6 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Award size={14} className="text-primary" /> {filtered.length} verified hospitals</div>
            <div className="flex items-center gap-2"><TrendingUp size={14} className="text-primary" /> Average score {avg}</div>
            <div className="flex items-center gap-2"><StarIcon size={14} className="fill-[#EAB308] text-[#EAB308]" /> HCAHPS Fact Sheet, Apr 2022</div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="rounded-lg border border-border bg-white p-16 text-center text-muted-foreground">Loading rankings…</div>
        ) : (
          <>
            {/* Podium */}
            {podium.length ? (
              <div className="grid gap-4 md:grid-cols-3" data-testid="hcahps-podium">
                {podium.map((h, i) => (
                  <button
                    key={h.id}
                    onClick={() => setDialogHospital(h)}
                    type="button"
                    className="group relative text-left rounded-lg border border-border bg-white p-5 hover:border-primary/40 hover:shadow-sm transition-all"
                    data-testid={`hcahps-podium-${i}`}
                  >
                    <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-muted-foreground">Rank</div>
                    <div className="absolute top-6 right-3 font-serif text-3xl text-primary">#{i + 1}</div>
                    <div className={"inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest " + (TIER_STYLE[h.tier] || TIER_STYLE.bronze)}>
                      {TIER_LABEL[h.tier] || h.tier || "Verified"}
                    </div>
                    <div className="mt-3 font-serif text-lg text-primary leading-snug pr-10">{h.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {h.city}{h.state && h.state !== "Other" ? `, ${h.state}` : ""} · {h.beds} beds</div>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <div className="font-serif text-4xl text-primary">{h.hcahps_score}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">HCAHPS score</div>
                      </div>
                      <HcahpsStars stars={h.hcahps_stars} size={16} />
                    </div>
                    <div className="mt-3 text-[11px] text-primary/70 opacity-0 group-hover:opacity-100 transition-opacity">See all 10 measures →</div>
                  </button>
                ))}
              </div>
            ) : null}

            {/* View toggle + Filters */}
            <div className="mt-10 rounded-lg border border-border bg-white p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40" data-testid="hcahps-view-toggle">
                  <button
                    type="button"
                    onClick={() => setView("overall")}
                    className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 ${view === "overall" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
                    data-testid="hcahps-view-overall"
                  >
                    <LayoutGrid size={12} /> Overall
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("by-state")}
                    className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 ${view === "by-state" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
                    data-testid="hcahps-view-state"
                  >
                    <MapPin size={12} /> By state
                  </button>
                </div>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search hospital, city or state…"
                    className="pl-9"
                    data-testid="hcahps-search"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={13} className="text-muted-foreground" />
                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger className="h-9 w-40" data-testid="hcahps-state"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>
                      {states.map((s) => (
                        <SelectItem key={s} value={s}>{s === "all" ? "All states" : s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger className="h-9 w-40" data-testid="hcahps-city"><SelectValue placeholder="City" /></SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c} value={c}>{c === "all" ? "All cities" : c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger className="h-9 w-36" data-testid="hcahps-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
                    <SelectContent>
                      {TIERS.map((t) => (
                        <SelectItem key={t} value={t}>{t === "all" ? "All tiers" : TIER_LABEL[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(q || city !== "all" || tier !== "all" || stateFilter !== "all") && (
                  <Button variant="outline" size="sm" onClick={() => { setQ(""); setCity("all"); setTier("all"); setStateFilter("all"); }} data-testid="hcahps-clear">
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Body — overall list OR by-state groups */}
            {view === "overall" ? (
              <div className="mt-4 rounded-lg border border-border bg-white overflow-hidden">
                <div className="grid grid-cols-[60px_1fr_140px_140px_120px_100px] items-center px-4 py-3 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">
                  <div>Rank</div>
                  <div>Hospital</div>
                  <div className="hidden md:block">State</div>
                  <div className="hidden md:block text-right">Tier</div>
                  <div className="text-right">Stars</div>
                  <div className="text-right">Score</div>
                </div>
                {rest.length === 0 && podium.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground">No hospitals match your filters.</div>
                ) : null}
                {rest.map((h, i) => (
                  <button
                    key={h.id}
                    onClick={() => setDialogHospital(h)}
                    type="button"
                    className="w-full text-left grid grid-cols-[60px_1fr_140px_140px_120px_100px] items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors"
                    data-testid={`hcahps-row-${i + 3}`}
                  >
                    <div className="text-sm text-muted-foreground">#{i + 4}</div>
                    <div>
                      <div className="text-sm text-primary">{h.name}</div>
                      <div className="text-[11px] text-muted-foreground md:hidden">{h.city}{h.state && h.state !== "Other" ? `, ${h.state}` : ""} · {TIER_LABEL[h.tier] || h.tier}</div>
                    </div>
                    <div className="hidden md:block text-xs text-muted-foreground">{h.state && h.state !== "Other" ? h.state : h.city}</div>
                    <div className="hidden md:flex justify-end">
                      <span className={"inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest " + (TIER_STYLE[h.tier] || TIER_STYLE.bronze)}>
                        {TIER_LABEL[h.tier] || h.tier}
                      </span>
                    </div>
                    <div className="flex justify-end"><HcahpsStars stars={h.hcahps_stars} size={12} /></div>
                    <div className="text-right font-serif text-lg text-primary">{h.hcahps_score}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-4 space-y-6" data-testid="hcahps-by-state">
                {byState.length === 0 ? (
                  <div className="rounded-lg border border-border bg-white p-10 text-center text-sm text-muted-foreground">
                    No hospitals match your filters.
                  </div>
                ) : (
                  byState.map((group) => (
                    <div key={group.state} className="rounded-lg border border-border bg-white overflow-hidden" data-testid={`hcahps-state-${group.state}`}>
                      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <div>
                          <div className="font-serif text-lg text-primary">{group.state}</div>
                          <div className="text-[11px] text-muted-foreground">{group.hospitals.length} hospital{group.hospitals.length === 1 ? "" : "s"} · avg score {group.avg}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">State leader</div>
                          <div className="text-sm text-primary">{group.top?.name}</div>
                        </div>
                      </div>
                      <div className="divide-y divide-border">
                        {group.hospitals.map((h, i) => (
                          <button
                            key={h.id}
                            onClick={() => setDialogHospital(h)}
                            type="button"
                            className="w-full text-left grid grid-cols-[40px_1fr_120px_100px_80px] items-center px-4 py-2.5 hover:bg-muted/40 transition-colors"
                            data-testid={`hcahps-state-row-${group.state}-${i}`}
                          >
                            <div className="text-xs text-muted-foreground">#{i + 1}</div>
                            <div>
                              <div className="text-sm text-primary">{h.name}</div>
                              <div className="text-[11px] text-muted-foreground">{h.city}</div>
                            </div>
                            <div className="hidden md:flex justify-end">
                              <span className={"inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest " + (TIER_STYLE[h.tier] || TIER_STYLE.bronze)}>
                                {TIER_LABEL[h.tier] || h.tier}
                              </span>
                            </div>
                            <div className="flex justify-end"><HcahpsStars stars={h.hcahps_stars} size={11} /></div>
                            <div className="text-right font-serif text-base text-primary">{h.hcahps_score}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="mt-6 text-[11px] text-muted-foreground leading-relaxed max-w-3xl">
              HCAHPS (Hospital Consumer Assessment of Healthcare Providers and Systems) is a standardised patient-perception instrument published by CMS. Star ratings on this page follow the HCAHPS Star Rating scale (April 2022 fact sheet). Individual scores are computed from public data plus verified provider submissions. Nothing on this page is sponsored or paid.
            </div>
          </>
        )}
      </section>

      <Footer />

      <HcahpsHospitalDialog
        hospital={dialogHospital}
        allHospitals={rows}
        open={!!dialogHospital}
        onOpenChange={(o) => { if (!o) setDialogHospital(null); }}
      />
    </div>
  );
}
